import React from 'react';
import { useNavigate } from 'react-router-dom';
import './ErrorFallback.css';

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
  context?: string;
  showDetails?: boolean;
  onRetry?: () => void;
  onReport?: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetError,
  context = 'application',
  showDetails = false,
  onRetry,
  onReport
}) => {
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      resetError();
    }
  };

  const handleReport = () => {
    if (onReport) {
      onReport();
    } else {
      // Default error reporting
      console.error('User reported error:', error);
    }
  };

  return (
    <div className="error-fallback">
      <div className="error-content">
        <div className="error-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </div>
        
        <h2 className="error-title">Something went wrong</h2>
        <p className="error-message">
          We're sorry, but something unexpected happened in the {context}.
        </p>
        
        {showDetails && (
          <details className="error-details">
            <summary>Technical Details</summary>
            <pre className="error-stack">{error.message}</pre>
          </details>
        )}
        
        <div className="error-actions">
          <button 
            className="btn btn-primary"
            onClick={handleRetry}
            aria-label="Try again"
          >
            Try Again
          </button>
          
          <button 
            className="btn btn-secondary"
            onClick={handleReport}
            aria-label="Report this error"
          >
            Report Issue
          </button>
          
          <button 
            className="btn btn-ghost"
            onClick={() => window.location.reload()}
            aria-label="Reload the page"
          >
            Reload Page
          </button>
        </div>
      </div>
    </div>
  );
};

// Specialized error fallbacks for different contexts
export const NetworkErrorFallback: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <div className="error-fallback network-error">
    <div className="error-content">
      <div className="error-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          <polyline points="3.27,6.96 12,12.01 20.73,6.96"/>
          <line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
      </div>
      
      <h2 className="error-title">Connection Problem</h2>
      <p className="error-message">
        We're having trouble connecting to our servers. Please check your internet connection and try again.
      </p>
      
      <div className="error-actions">
        <button className="btn btn-primary" onClick={onRetry}>
          Retry Connection
        </button>
      </div>
    </div>
  </div>
);

export const DataErrorFallback: React.FC<{ onRetry: () => void; dataType: string }> = ({ onRetry, dataType }) => (
  <div className="error-fallback data-error">
    <div className="error-content">
      <div className="error-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14,2 14,8 20,8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10,9 9,9 8,9"/>
        </svg>
      </div>
      
      <h2 className="error-title">Data Loading Error</h2>
      <p className="error-message">
        We couldn't load your {dataType}. This might be a temporary issue.
      </p>
      
      <div className="error-actions">
        <button className="btn btn-primary" onClick={onRetry}>
          Reload {dataType}
        </button>
      </div>
    </div>
  </div>
);

export const PermissionErrorFallback: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <PermissionErrorFallbackContent onRetry={onRetry} />
);

const PermissionErrorFallbackContent: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const navigate = useNavigate();

  return (
    <div className="error-fallback permission-error">
      <div className="error-content">
        <div className="error-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <circle cx="12" cy="16" r="1"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        
        <h2 className="error-title">Access Denied</h2>
        <p className="error-message">
          You don't have permission to access this resource. Please check your account status or contact support.
        </p>
        
        <div className="error-actions">
          <button className="btn btn-primary" onClick={onRetry}>
            Check Again
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/profile')}>
            Go to Profile
          </button>
        </div>
      </div>
    </div>
  );
};

export const OfflineErrorFallback: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <OfflineErrorFallbackContent onRetry={onRetry} />
);

const OfflineErrorFallbackContent: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const navigate = useNavigate();

  return (
    <div className="error-fallback offline-error">
      <div className="error-content">
        <div className="error-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="2" y1="2" x2="22" y2="22"/>
            <path d="M16 8h6a2 2 0 0 1 0 4h-2"/>
            <path d="M2 8h2"/>
            <path d="M2 12h4"/>
            <path d="M2 16h2"/>
          </svg>
        </div>
        
        <h2 className="error-title">You're Offline</h2>
        <p className="error-message">
          It looks like you're not connected to the internet. Some features may not be available.
        </p>
        
        <div className="error-actions">
          <button className="btn btn-primary" onClick={onRetry}>
            Check Connection
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/')}>
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
